/**
 * OIDC Authorization Code + PKCE against Keycloak.
 *
 * Tokens live in memory only — never localStorage/sessionStorage, so an XSS
 * cannot read them and closing the tab ends the client-side session. The cost
 * is that a hard reload has nothing to restore, which is why the app opens with
 * a `prompt=none` attempt: if Keycloak still has an SSO session, that returns a
 * fresh code with no user interaction; if not, it returns `login_required` and
 * we show the sign-in screen.
 *
 * The refresh token (also in memory) drives a timer that renews the access
 * token before it expires, so a working session never blinks.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { OIDC_CLIENT_ID, OIDC_ISSUER } from './runtimeEnv.js';

const AuthContext = createContext(null);

const config = {
  issuer: OIDC_ISSUER,
  clientId: OIDC_CLIENT_ID,
  // The app root, derived — not configured: the platform bundle beside this one
  // returns to /platform/, and each environment's origin is simply where it is
  // served. (MESHHUB_OIDC_REDIRECT_URI used to be baked in at build time.)
  redirectUri: `${window.location.origin}/`,
};

const endpoints = {
  authorize: `${config.issuer}/protocol/openid-connect/auth`,
  token: `${config.issuer}/protocol/openid-connect/token`,
  logout: `${config.issuer}/protocol/openid-connect/logout`,
};

/** PKCE lives in sessionStorage only between the redirect and the callback. */
const PKCE_KEY = 'meshhub.pkce';
const SILENT_ATTEMPTED_KEY = 'meshhub.silentAttempted';
/**
 * Keycloak always returns to the registered redirect URI ("/"), so the path the
 * user actually asked for has to be carried across the round trip ourselves —
 * otherwise every deep link silently becomes the home page.
 */
const RETURN_TO_KEY = 'meshhub.returnTo';

function randomString(bytes = 32) {
  const buffer = new Uint8Array(bytes);
  crypto.getRandomValues(buffer);
  return base64Url(buffer);
}

function base64Url(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

async function challengeFor(verifier) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(digest);
}

async function beginLogin({ silent }) {
  const verifier = randomString();
  const state = randomString(16);
  sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state }));
  sessionStorage.setItem(RETURN_TO_KEY, window.location.pathname + window.location.search);

  const params = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    response_type: 'code',
    scope: 'openid profile email',
    state,
    code_challenge: await challengeFor(verifier),
    code_challenge_method: 'S256',
  });
  // prompt=none asks Keycloak to reuse an existing SSO session or fail fast,
  // which is what makes a hard reload seamless without storing tokens.
  if (silent) params.set('prompt', 'none');

  window.location.assign(`${endpoints.authorize}?${params}`);
}

async function exchangeCode(code, verifier) {
  const response = await fetch(endpoints.token, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      code,
      code_verifier: verifier,
    }),
  });
  if (!response.ok) throw new Error(`token exchange failed: ${response.status}`);
  return response.json();
}

async function refreshTokens(refreshToken) {
  const response = await fetch(endpoints.token, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: config.clientId,
      refresh_token: refreshToken,
    }),
  });
  if (!response.ok) throw new Error(`refresh failed: ${response.status}`);
  return response.json();
}

/** Decode a JWT payload for display only — the BFF does the real verification. */
function readClaims(accessToken) {
  try {
    const payload = accessToken.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return {};
  }
}

/** Module-level so the Apollo link can read it without a React render. */
let currentAccessToken = null;
export const getAccessToken = () => currentAccessToken;

/**
 * The OIDC callback must be handled exactly once per page load, and the
 * authorization code and PKCE verifier are both single-use. StrictMode invokes
 * effects twice in development, which would consume the verifier on the first
 * pass and then fail on the second — so the guard lives at module scope, where
 * "once per page load" is actually true, rather than in component state.
 */
let bootstrapStarted = false;

export function AuthProvider({ children }) {
  const [status, setStatus] = useState('loading'); // loading | anonymous | authenticated
  const [claims, setClaims] = useState(null);
  const [error, setError] = useState(null);
  const refreshTokenRef = useRef(null);
  const timerRef = useRef(null);

  const applyTokens = useCallback((tokens) => {
    currentAccessToken = tokens.access_token;
    refreshTokenRef.current = tokens.refresh_token ?? refreshTokenRef.current;
    setClaims(readClaims(tokens.access_token));
    setStatus('authenticated');

    // Renew a little early so an in-flight request never carries a dead token.
    clearTimeout(timerRef.current);
    const lifetimeMs = Math.max((tokens.expires_in ?? 300) - 30, 30) * 1000;
    timerRef.current = setTimeout(async () => {
      try {
        applyTokens(await refreshTokens(refreshTokenRef.current));
      } catch {
        // The refresh token expired or was revoked: fall back to a silent
        // attempt, which either restores the session or shows the login screen.
        currentAccessToken = null;
        sessionStorage.removeItem(SILENT_ATTEMPTED_KEY);
        beginLogin({ silent: true });
      }
    }, lifetimeMs);
  }, []);

  useEffect(() => {
    if (bootstrapStarted) return undefined;
    bootstrapStarted = true;

    async function bootstrap() {
      const url = new URL(window.location.href);
      const code = url.searchParams.get('code');
      const returnedState = url.searchParams.get('state');
      const oidcError = url.searchParams.get('error');

      // --- returning from Keycloak ---
      if (code || oidcError) {
        const stored = JSON.parse(sessionStorage.getItem(PKCE_KEY) || 'null');
        sessionStorage.removeItem(PKCE_KEY);
        // Strip the OIDC parameters and restore the path that was asked for,
        // before the router mounts and reads the location.
        const returnTo = sessionStorage.getItem(RETURN_TO_KEY) || url.pathname;
        sessionStorage.removeItem(RETURN_TO_KEY);
        window.history.replaceState({}, '', returnTo);

        if (oidcError) {
          // login_required is the expected answer to prompt=none for someone
          // with no SSO session — not an error worth showing.
          if (oidcError !== 'login_required' && oidcError !== 'interaction_required') {
            setError(url.searchParams.get('error_description') || oidcError);
          }
          setStatus('anonymous');
          return;
        }

        if (!stored || stored.state !== returnedState) {
          setError('پاسخ ورود معتبر نبود. دوباره تلاش کنید.');
          setStatus('anonymous');
          return;
        }

        try {
          applyTokens(await exchangeCode(code, stored.verifier));
        } catch (err) {
          setError(err.message);
          setStatus('anonymous');
        }
        return;
      }

      // --- fresh load: try to reuse an existing Keycloak SSO session once ---
      if (!sessionStorage.getItem(SILENT_ATTEMPTED_KEY)) {
        sessionStorage.setItem(SILENT_ATTEMPTED_KEY, '1');
        await beginLogin({ silent: true });
        return; // navigating away
      }
      setStatus('anonymous');
    }

    bootstrap();
    // No cleanup that cancels the exchange: the guard above already makes this
    // run once, and aborting a half-finished exchange would strand the session.
    return undefined;
  }, [applyTokens]);

  const login = useCallback(() => {
    setError(null);
    sessionStorage.removeItem(SILENT_ATTEMPTED_KEY);
    beginLogin({ silent: false });
  }, []);

  const logout = useCallback(() => {
    const idToken = currentAccessToken;
    currentAccessToken = null;
    refreshTokenRef.current = null;
    clearTimeout(timerRef.current);
    sessionStorage.removeItem(SILENT_ATTEMPTED_KEY);
    setStatus('anonymous');
    setClaims(null);
    if (!idToken) return;
    const params = new URLSearchParams({
      client_id: config.clientId,
      post_logout_redirect_uri: config.redirectUri,
    });
    window.location.assign(`${endpoints.logout}?${params}`);
  }, []);

  const value = useMemo(
    () => ({
      status,
      error,
      login,
      logout,
      // Display only. The BFF's `me` query is the authority on role.
      displayName: claims?.name || claims?.preferred_username || '',
      username: claims?.preferred_username || '',
    }),
    [status, error, login, logout, claims],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
